// @givmo/app-charity — the claim wizard's bundled acceptance step (CPA-WEB12, D-CP-37).
//
// ★ WHAT THIS IS. Until now, claiming an organization and accepting the §318(a) Charity Terms were two
// separate acts, and the second was a dismissible nudge on the dashboard — so an organization could sit
// CLAIMED but never CONSENTED indefinitely. That state governs send windows, disclosure and Hawaii
// legality, so D-CP-37 makes claiming ONE flow that ends in a recorded consent: the agreement is read
// in the wizard, and the single submit carries the acceptance with it.
//
// ★ TWO AFFIRMATIONS, NOT ONE — and they are different acts, which is why they are different boxes.
// The first is about the PERSON ("I am an authorized representative of this organization"); the second
// is made BY THE ORGANIZATION through them ("on behalf of <name>, I accept these Terms"). CLAIMED and
// CONSENTED stay distinct recorded states inside the one flow, and a single combined checkbox would
// have collapsed a legal act into a formality. Do not merge them.
//
// ★ ORDER: identification, then authority, then consent. The role and the title come FIRST because they
// are what gives the authority affirmation its content — "I am an authorized representative" says
// nothing until the reader has stated in what capacity. §12599.9(f)(1) enumerates exactly four statuses
// and the picker offers exactly those four, undefaulted: a pre-selected role would put a claim about a
// person's authority into a legal record that the person never made.
//
// LEGAL RAILS:
//   - The agreement's bytes, version and hash all come from ONE read
//     (`GET /charity-admin/charities/{id}/claim/terms`). No legal text is authored here, no version is
//     constructed or defaulted, and the body is rendered by the same conservative presenter the served
//     Terms page uses (`terms-document-window.tsx`).
//   - THE VERSION NUMBER IS A RECORD TOO, NOT DISPLAY (D-CP-84, CPA-WEB78). The consent names the
//     document as "the Charity Terms above" — the one on screen — and the submit echoes its version
//     and hash, which is what pins the recorded acceptance to it. Neither the window nor the consent
//     sentence prints the number, as on the served Terms page (D-CP-80).
//   - THE CONTENT HASH IS A RECORD, NOT DISPLAY. It rides the submit so the acceptance row pins exactly
//     the bytes that were on screen; it is never rendered. A sha256 tells a small nonprofit nothing.
//     Do not add a hash chip, a "verify this document" affordance, or a receipt hash line.
//   - The caller echoes the version/hash of the document IT DISPLAYED — see `claim-wizard-page.tsx`,
//     which captures the rendered object at submit rather than re-reading. Re-fetching at submit is how
//     somebody consents to one document and is recorded against another.
//
// NOT BUILT, DELIBERATELY: the §318(a)(3) per-platform give/withhold control. GC round 3 (G13) rules it
// a TRIGGER, not a gate — Schedule A is EMPTY at V1.1, and any Schedule-A entry is itself a new-version
// event that rides the §318(a)(2) change-notice cycle. An all-or-nothing consent control here is
// expressly out of scope; do not add one because the statute mentions a selection.

import { Card, CardBody, Checkbox, Field, Icon, Select, testIdProp } from '@givmo/design-system';
import type { CharityTermsOut } from '@givmo/api-client';
import { ShieldCheck } from 'lucide-react';
import { TermsDocumentWindow } from '../terms/terms-document-window';
import { SIGNER_ROLE_OPTIONS, type SignerRole } from './claim-vocab';

/** What the claimant has stated so far. Owned by the wizard page; this block only renders it. */
export interface SigningState {
  /** The (f)(1) status the claimant represents. `null` until they choose — never defaulted. */
  role: SignerRole | null;
  /** Their real job title, as typed (untrimmed; {@link signingSubmission} trims). */
  title: string;
  /** "I am an authorized representative of …" — the claim about the PERSON. */
  authorityAffirmed: boolean;
  /** "On behalf of … I accept the Charity Terms" — the act done BY THE ORGANIZATION. */
  termsAccepted: boolean;
}

export const EMPTY_SIGNING_STATE: SigningState = {
  role: null,
  title: '',
  authorityAffirmed: false,
  termsAccepted: false,
};

/**
 * The four values the submit must carry, or `null` when the ceremony is not complete.
 *
 * ONE function answers both "may the button fire?" and "what goes in the body", so the gate and the
 * payload can never disagree — a gate that permitted a submit the body could not fill would produce the
 * backend's 409 with nothing on screen explaining it, and the reverse would silently drop a field into
 * a §318(a) record as `null`.
 *
 * Every condition is also a backend requirement: a submit missing any of the four is refused. Nothing
 * here is client-side courtesy.
 */
export function signingSubmission(
  state: SigningState,
  terms: CharityTermsOut | null,
): {
  terms_version_number: number;
  terms_content_hash: string;
  signer_role: SignerRole;
  signer_title: string;
} | null {
  if (terms === null) return null;
  if (!state.authorityAffirmed || !state.termsAccepted) return null;
  if (state.role === null) return null;
  const title = state.title.trim();
  if (title.length === 0) return null;
  return {
    terms_version_number: terms.version_number,
    terms_content_hash: terms.content_hash,
    signer_role: state.role,
    signer_title: title,
  };
}

/**
 * The document + the signing controls.
 *
 * `terms` is the object CURRENTLY RENDERED, and the same object the caller echoes. The two are the same
 * reference by construction: the page passes its query data here and reads its query data at submit.
 */
export function ClaimSigningBlock({
  prefix,
  legalName,
  donorName = null,
  einDisplay,
  terms,
  state,
  onChange,
  disabled,
}: {
  /** The wizard's testid prefix (`claim-wizard`), so ids read as one screen's family. */
  prefix: string;
  legalName: string;
  /**
   * The name donors see, when it differs from `legalName`: the display name while one is live, which
   * the organization card above shows. One line ties the two together beside the affirmations;
   * `null` (no display name) shows none.
   */
  donorName?: string | null;
  /** The formatted EIN, or the empty string when the catalog row carries none. */
  einDisplay: string;
  terms: CharityTermsOut;
  state: SigningState;
  onChange: (next: SigningState) => void;
  disabled: boolean;
}) {
  return (
    <>
      {/* The agreement, readable in flow. A window rather than a page section: the affirmations below
          must stay on screen while it is read, which is also how the served Terms page is composed. */}
      <TermsDocumentWindow
        terms={terms}
        testIdPrefix={`${prefix}-terms`}
        heading="The Charity Terms you are accepting"
        hint="Scroll inside this box to read the whole agreement."
        className="flex max-h-[420px] flex-col overflow-hidden"
      />

      {/* THE CEREMONY — the loudest block on the screen, as the attestation alone used to be. */}
      <Card className="border-2 border-brand-500 bg-brand-50" testId={`${prefix}-signing`}>
        <CardBody className="flex flex-col gap-20 px-20 py-20">
          <div className="flex items-start gap-12">
            <Icon
              icon={ShieldCheck}
              size={18}
              className="mt-2 shrink-0 text-brand-700"
              aria-hidden
            />
            {/* The organization is NOT named here, deliberately. Both affirmations below name it in
                full, which is where the specificity legally matters — and a 77-character legal name in
                this label is a fourth repetition that costs three lines at 375px directly above the two
                sentences the reader most needs to read. */}
            <p className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong">
              Sign on behalf of your organization
            </p>
          </div>

          <div className="flex flex-col gap-16 sm:flex-row sm:gap-12">
            <Select
              appearance="saas"
              className="sm:flex-1"
              label="Your role"
              placeholder="Choose your role"
              options={SIGNER_ROLE_OPTIONS as { value: string; label: string }[]}
              value={state.role}
              onValueChange={(value) => onChange({ ...state, role: asSignerRole(value) })}
              disabled={disabled}
              required
              testId={`${prefix}-signer-role`}
            />
            <Field
              containerClassName="sm:flex-1"
              label="Your title"
              /* "e.g." because a bare plausible job title in a placeholder reads as a
                 pre-filled answer, and this one lands in a §318(a) record. */
              placeholder="e.g. Executive Director"
              /* No length cap: the column is `Text` server-side, and a cap invented here would be this
                 screen deciding what a nonprofit may call its own officers. */
              value={state.title}
              onChange={(event) => onChange({ ...state, title: event.target.value })}
              disabled={disabled}
              required
              autoComplete="organization-title"
              testId={`${prefix}-signer-title`}
            />
          </div>

          <div className="flex flex-col gap-14">
            {/* Read before the bold name below: the card above names the organization as donors
                see it, the affirmations as the agreement does (CPA-BE82). Only while the two differ. */}
            {donorName ? (
              <p
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${prefix}-name-connector`)}
              >
                “{legalName}” is shown to donors as “{donorName}”.
              </p>
            ) : null}
            {/* min-h-24 keeps each ACTIVATION target (the whole label row) at the WCAG 2.2 SC 2.5.8
                24px minimum — the SaaS box itself is 18px. Both rows are legal affirmations, so they
                get the same treatment the served Terms page's affirmation carries. */}
            <label
              className="flex min-h-24 cursor-pointer items-start gap-12"
              {...testIdProp(`${prefix}-authority-row`)}
            >
              <span className="mt-2">
                <Checkbox
                  appearance="saas"
                  checked={state.authorityAffirmed}
                  onCheckedChange={(checked) => onChange({ ...state, authorityAffirmed: checked })}
                  disabled={disabled}
                  testId={`${prefix}-authority-checkbox`}
                />
              </span>
              <span className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong">
                I am an authorized representative of <span className="font-w600">{legalName}</span>
                {einDisplay ? (
                  <>
                    , EIN <span className="font-w600 tabular-nums">{einDisplay}</span>
                  </>
                ) : null}
                , and the information I provide is accurate.
              </span>
            </label>

            <label
              className="flex min-h-24 cursor-pointer items-start gap-12"
              {...testIdProp(`${prefix}-consent-row`)}
            >
              <span className="mt-2">
                <Checkbox
                  appearance="saas"
                  checked={state.termsAccepted}
                  onCheckedChange={(checked) => onChange({ ...state, termsAccepted: checked })}
                  disabled={disabled}
                  testId={`${prefix}-consent-checkbox`}
                />
              </span>
              {/* The consent names the document it is a consent to — "the Charity Terms above", the one
                  on screen — and the submit echoes that document's version and hash into the record
                  (D-CP-8). The number itself is not printed (D-CP-84). */}
              <span className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-strong">
                On behalf of <span className="font-w600">{legalName}</span>, I accept the Charity
                Terms above.
              </span>
            </label>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

/**
 * Narrow the Select's `string | null` back to the closed set.
 *
 * The options ARE the four values, so an unknown string is unreachable through the UI — but the type
 * boundary is where the closed set is enforced, and enforcing it here means a widened option list can
 * never quietly put an unrecognized role into a §318(a) record.
 */
function asSignerRole(value: string | null): SignerRole | null {
  const match = SIGNER_ROLE_OPTIONS.find((option) => option.value === value);
  return match?.value ?? null;
}
